// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    CartLineModel,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import ProductAvailabilityModal, {
    ProductAvailabilityModalStyles,
} from "@pace/App/Components/Product/ProductAvailabilityModal";
import ProductTooltip, { ProductTooltipProps, ProductTooltipStyles } from "@pace/App/Components/Product/ProductTooltip";
import xUseAddToCart from "@pace/App/Hooks/xUseAddToCart";
import IconCheckmarkCircle from "@pace/XDL/Icons/IconCheckmarkCircle";
import IconXCircle from "@pace/XDL/Icons/IconXCircle";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface AvailabilityResult {
    qtyInCustomerBranch: string;
    qtyInDc: string;
    qtyInNetwork: string;
}
interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    // XNG-Change
    externalDoc?: string;
    packDescription?: string;
    availabilityResult?: AvailabilityResult | null;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    /* XNG-Change */
    isAuthenticated: state.context.session.isAuthenticated,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    // XNG-Change
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

// XNG-Change
type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // XNG-Change
    actionsGridItem?: GridItemProps;
    buttonsContainer?: GridItemProps;
    infoTooltip?: ProductTooltipStyles;
    availabilityMessagesResult?: InjectableCss;
    messageLabel?: TypographyPresentationProps;
    qtyValue?: TypographyPresentationProps;
    productAvailabilityModalStyles?: ProductAvailabilityModalStyles;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: { width: 12 },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    padding: 5px;
    border-radius: 5px;
    background-color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(0.2).string()};
    ${IconWrapper} {
        margin-right: 10px;
    }
    /* XNG-Change */
    ${injectCss}
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }
    // XNG-Change: change icons
    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        // return X;
        return IconXCircle;
    }
    // XNG-Change: there is no low stock message type.
    // if (availabilityType === AvailabilityMessageType.LowStock) {
    //     return AlertTriangle;
    // }

    // return Check;
    return IconCheckmarkCircle;
};

// XngChange: Reformat availability result
const reformatAvailabilityResult = (availabilityResult: AvailabilityResult) => {
    // we need in branch and DC
    const { qtyInDc = "0", qtyInCustomerBranch = "0" } = availabilityResult;
    return {
        dc: {
            icon:
                +qtyInDc === 0
                    ? {
                          src: IconXCircle,
                          color: "danger",
                      }
                    : {
                          src: IconCheckmarkCircle,
                          color: "success",
                      },
            result: qtyInDc,
        },
        customerBranch: {
            icon:
                +qtyInCustomerBranch === 0
                    ? {
                          src: IconXCircle,
                          color: "danger",
                      }
                    : {
                          src: IconCheckmarkCircle,
                          color: "success",
                      },
            result: qtyInCustomerBranch,
        },
    };
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse || 
        /* XNG-Change: Don't show this link in case of special order / out of stock */
        availability.messageType === AvailabilityMessageType.OutOfStock
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

/* XNG-Change: Add Important tooltip, Change message, structure, styles */
const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    // XNG-Change
    externalDoc,
    packDescription,
    availabilityResult,
    isAuthenticated,
    productContext,
    addToCart,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    /* XNG-Change: move this logic into modal component */
    // const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    // const [errorMessage, setErrorMessage] = useState("");
    // const [isLoading, setIsLoading] = useState(false);

    // XNG-Change: format the available result
    const formattedAvailabilityResult = availabilityResult ? reformatAvailabilityResult(availabilityResult) : null;

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    // XNG-Change: move this logic into modal component
    const viewAvailabilityByWarehouseClickHandler = () => {
        // setIsLoading(true);
        setModalIsOpen(true);
        // getRealTimeWarehouseInventory({
        //     productId,
        //     unitOfMeasure,
        //     expand: ["warehouses"],
        //     onComplete: result => {
        //         setWarehouses(result.warehouses);
        //         setErrorMessage(result.errorMessage);
        //         setIsLoading(false);
        //     },
        // });
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    // XNG-Change
    const { addToCartClickHandler, canAddToCart, isAddToCartButtonDisabled } = xUseAddToCart({
        productContext,
        addToCartChainHandler: addToCart,
    });

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    /* XNG-Change: Add externalDoc info as tooltip from product model, Add pack that will add a pack into cart */
    const tooltips = (
        <>
            {!!externalDoc && !isProductDetailsPage && (
                <ProductTooltip tooltipText={externalDoc} extendedStyles={styles.infoTooltip} />
            )}
        </>
    );

    // XNG-Change Add tooltips
    // if (failedToLoadInventory) {
    //     return (
    //         <StyledWrapper {...styles.errorWrapper}>
    //             <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
    //         </StyledWrapper>
    //     );
    // }

    if (failedToLoadInventory) {
        return (
            <GridContainer {...styles.container}>
                <GridItem {...styles.messageGridItem}>
                    <InventoryMessage color="danger" {...styles.inventoryMessage}>
                        <Icon color="danger" src={IconXCircle} />
                        <Typography {...styles.messageText}>
                            {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                        </Typography>
                    </InventoryMessage>
                </GridItem>
                <GridItem {...styles.actionsGridItem}>
                    <GridItem {...styles.buttonsContainer}>{tooltips}</GridItem>
                </GridItem>
            </GridContainer>
        );
    }
    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    /* XNG-Change: Show in DC and in branch */
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        if (formattedAvailabilityResult && availability.messageType !== AvailabilityMessageType.OutOfStock) {
            inventoryTextComponent = (
                <StyledWrapper {...styles.availabilityMessagesResult}>
                    <InventoryMessage color={color} {...styles.inventoryMessage}>
                        <Icon {...formattedAvailabilityResult.customerBranch.icon} />
                        <Typography {...styles.messageLabel}>{translate("In Branch:")}</Typography>
                        <Typography {...styles.qtyValue} data-test-selector="availabilityMessage">
                            {formattedAvailabilityResult.customerBranch.result}
                        </Typography>
                    </InventoryMessage>
                    <InventoryMessage color={color} {...styles.inventoryMessage}>
                        <Icon {...formattedAvailabilityResult.dc.icon} />
                        <Typography {...styles.messageLabel}>{translate("In DC:")}</Typography>
                        <Typography {...styles.qtyValue} data-test-selector="availabilityMessage">
                            {formattedAvailabilityResult.dc.result}
                        </Typography>
                    </InventoryMessage>
                </StyledWrapper>
            );
        } else {
            inventoryTextComponent = (
                <InventoryMessage color={color} {...styles.inventoryMessage}>
                    <Icon color={color} src={iconSrc} />
                    <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                        {/* XNG-Change: Handle special order message */}
                        {availability.message}
                    </Typography>
                </InventoryMessage>
            );
        }
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    return (
        <GridContainer {...styles.container}>
            {/* XNG-Change: show if and only if isAuthenticated */}
            <GridItem {...styles.messageGridItem}>{isAuthenticated && inventoryTextComponent}</GridItem>
            {/* XNG-Change: Add pack, Important info */}
            <GridItem {...styles.actionsGridItem}>
                <GridItem {...styles.buttonsContainer}>
                    {/* XNG-Change: show if and only if isAuthenticated */}
                    {isAuthenticated && showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
                        <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                            <Link
                                {...styles.availabilityByWarehouseLink}
                                onClick={viewAvailabilityByWarehouseClickHandler}
                                data-test-selector="availabilityByWarehouseLink"
                            >
                                {translate("View  All Store Availability")}
                            </Link>
                            {/* XNG-Change: move modal into new component */}
                            <ProductAvailabilityModal
                                modalIsOpen={modalIsOpen}
                                modalCloseHandler={modalCloseHandler}
                                productId={productId}
                                unitOfMeasure={unitOfMeasure}
                                extendedStyles={styles.productAvailabilityModalStyles}
                            />
                            {/* <Modal
                                {...styles.availabilityByWarehouseModal}
                                headline={translate("Warehouse Inventory")}
                                isOpen={modalIsOpen}
                                handleClose={modalCloseHandler}
                            >
                                {isLoading && (
                                    <StyledWrapper {...styles.centeringWrapper}>
                                        <LoadingSpinner {...styles.spinner} />
                                    </StyledWrapper>
                                )}
                                {!isLoading && (
                                    <>
                                        {errorMessage ? (
                                            <div>{errorMessage}</div>
                                        ) : (
                                            <DataTable
                                                {...styles.availabilityByWarehouseTable}
                                                data-test-selector="availabilityByWarehouseModal"
                                            >
                                                <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                        {translate("Warehouse")}
                                                    </DataTableHeader>
                                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                        {translate("QTY")}
                                                    </DataTableHeader>
                                                </DataTableHead>
                                                <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                                    {warehouses?.map(warehouse => (
                                                        <DataTableRow
                                                            {...styles.availabilityByWarehouseTableRow}
                                                            key={warehouse.name}
                                                        >
                                                            <DataTableCell
                                                                {...styles.availabilityByWarehouseTableCell}
                                                                data-test-selector="warehouseAvailabilityModal_Description"
                                                            >
                                                                {warehouse.description || warehouse.name}
                                                            </DataTableCell>
                                                            <DataTableCell
                                                                {...styles.availabilityByWarehouseTableCell}
                                                                data-test-selector="warehouseAvailabilityModal_Quantity"
                                                            >
                                                                {warehouse.qty}
                                                            </DataTableCell>
                                                        </DataTableRow>
                                                    ))}
                                                </DataTableBody>
                                            </DataTable>
                                        )}
                                    </>
                                )}
                            </Modal> */}
                        </GridItem>
                    )}
                    {tooltips}
                </GridItem>
            </GridItem>
        </GridContainer>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAvailability));
